import React, { Component } from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon, HomeIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import ErrorPage from './ErrorPage';
import { logErrorBoundary, logger } from '../../utils/logger';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to centralized logging system
    logErrorBoundary(error, errorInfo);
    
    // Log additional context
    logger.error('Component error boundary triggered', {
      component: this.constructor.name,
      props: this.props,
      state: this.state,
      timestamp: new Date().toISOString()
    });
    
    this.setState({ errorInfo });
    // like Sentry, LogRocket, etc.
  }

  toggleDetails = () => {
    this.setState(prevState => ({ showDetails: !prevState.showDetails }));
  }

  render() {
    if (this.state.hasError) {
      // Check for specific error types
      const isNullToObjectError = 
        this.state.error?.message?.includes('null to object') ||
        this.state.error?.message?.includes('undefined to object');
        
      const isWebpackError = 
        this.state.error?.message?.includes('__WEBPACK_DEFAULT_EXPORT__') ||
        this.state.error?.toString().includes('__WEBPACK_DEFAULT_EXPORT__');
      
      // Set custom error message based on error type
      let errorMessage;
      let errorTitle;
      
      if (isNullToObjectError) {
        errorMessage = "Cannot convert undefined or null to object. This usually happens when the application tries to process data that's not in the expected format. Please try reloading the page or returning to the workspace.";
        errorTitle = 'Application Error';
      } else if (isWebpackError) {
        errorMessage = "Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization";
        errorTitle = 'Document Error';
      } else {
        errorMessage = this.state.error?.message || 'An unexpected error occurred';
        errorTitle = 'Application Error';
      }
      
      // Use the updated ErrorPage component with the right styling
      return (
        <ErrorPage
          title={errorTitle}
          message={errorMessage}
          error={this.state.error}
          showReload={true}
          showReturn={true}
          returnPath="/workspaces"
          returnText="Return to Workspace"
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 